import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { ReportsSearchFilterOptionType, ReportsSearchFilterType } from './SearchFilters';
import { assignValuesFromRouteQuery, pushValuesToRouteQuery } from '@/utils';
import XLight from '@/assets/xLight.svg';
import classNames from 'classnames';

type CustomFilter =
  | { label: string; key: string; keys?: never }
  | { label: string; key?: never; keys: string[]; separator?: string };

type SearchFilterChipsProps = {
  availableFilters?: ReportsSearchFilterType[];
  customFilters?: CustomFilter[];
  maxDisplayedChips?: number;
  className?: string;
  singleRow?: boolean;
  value?: { [key: string]: string[] } | null;
  onChange?: (value: { [key: string]: string[] }) => void;
};

type FilterChip = {
  filterKey?: string;
  filterKeys?: string[];
  optionId?: string;
  optionIds?: string[];
  label: string;
};

const getOptionsMap = (options: ReportsSearchFilterOptionType[], map: Record<string, string>) => {
  options.forEach((option) => {
    map[option.id] = option.name;
    if (option.options?.length) {
      getOptionsMap(option.options, map);
    }
  });
};

const SearchFilterChips = ({
  availableFilters,
  customFilters,
  maxDisplayedChips,
  className,
  singleRow = false,
  value,
  onChange,
}: SearchFilterChipsProps) => {
  const router = useRouter();
  const isControlled = value !== undefined;
  const controlledValues = useMemo(() => {
    if (!isControlled) {
      return undefined;
    }

    return value || {};
  }, [isControlled, value]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const moreLabelMeasureRef = useRef<HTMLSpanElement | null>(null);

  const [singleRowVisibleCount, setSingleRowVisibleCount] = useState<number | null>(null);

  const labelsByFilter = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};

    availableFilters?.forEach((filter) => {
      const optionsMap: Record<string, string> = {};
      getOptionsMap(filter.options, optionsMap);
      map[filter.key] = optionsMap;
    });

    return map;
  }, [availableFilters]);

  const chips = useMemo(() => {
    const allChips: FilterChip[] = [];

    const selectedFilterValues: Record<string, string[]> = {};

    availableFilters?.forEach((filter) => {
      selectedFilterValues[filter.key] = [];
    });

    if (controlledValues) {
      Object.keys(selectedFilterValues).forEach((filterKey) => {
        selectedFilterValues[filterKey] = controlledValues[filterKey] || [];
      });
    } else {
      assignValuesFromRouteQuery(router, selectedFilterValues);
    }

    Object.entries(selectedFilterValues).forEach(([filterKey, optionIds]) => {
      optionIds.forEach((optionId) => {
        allChips.push({
          filterKey,
          optionId,
          label: labelsByFilter[filterKey]?.[optionId] ?? optionId,
        });
      });
    });

    customFilters?.forEach((filter) => {
      if ('key' in filter && filter.key) {
        const customValue = controlledValues ? controlledValues[filter.key]?.[0] : (router.query[filter.key] as string | undefined);

        if (customValue) {
          allChips.push({
            filterKey: filter.key,
            optionId: customValue,
            label: `${filter.label}: ${customValue}`,
          });
        }
      } else if ('keys' in filter && filter.keys) {
        const optionIds: string[] = [];

        filter.keys.forEach((filterKey) => {
          const customValue = controlledValues
            ? controlledValues[filterKey]?.[0]
            : (router.query[filterKey] as string | undefined);

          if (customValue) {
            optionIds.push(customValue);
          }
        });

        if (optionIds.length > 0) {
          allChips.push({
            filterKeys: filter.keys,
            optionIds,
            label: `${filter.label}: ${optionIds.join(filter.separator || ', ')}`,
          });
        }
      }
    });

    return allChips;
  }, [availableFilters, labelsByFilter, router, customFilters, controlledValues]);

  const maxAllowedChips =
    typeof maxDisplayedChips === 'number' && maxDisplayedChips > 0
      ? Math.min(maxDisplayedChips, chips.length)
      : chips.length;

  useEffect(() => {
    if (!singleRow) {
      setSingleRowVisibleCount(null);
      return;
    }

    const calculateVisibleChips = () => {
      const container = containerRef.current;
      const measureContainer = measureRef.current;

      if (!container || !measureContainer) {
        return;
      }

      const chipElements = Array.from(measureContainer.querySelectorAll<HTMLElement>('[data-measure-chip]'));

      if (!chipElements.length) {
        setSingleRowVisibleCount(0);
        return;
      }

      const containerWidth = container.clientWidth;
      const gap = 8;
      const moreLabelWidth = moreLabelMeasureRef.current?.offsetWidth ?? 0;

      let usedWidth = 0;
      let visibleCount = 0;

      for (let index = 0; index < maxAllowedChips; index += 1) {
        const chip = chipElements[index];

        if (!chip) {
          break;
        }

        const chipWidth = chip.offsetWidth;
        const nextVisibleCount = visibleCount + 1;
        const remainingAfterThisChip = chips.length - nextVisibleCount;
        const shouldReserveMoreLabel = remainingAfterThisChip > 0;
        const gapBeforeChip = visibleCount > 0 ? gap : 0;
        const reservedMoreWidth = shouldReserveMoreLabel ? moreLabelWidth + gap : 0;

        const nextWidth = usedWidth + gapBeforeChip + chipWidth + reservedMoreWidth;

        if (nextWidth > containerWidth) {
          break;
        }

        usedWidth += gapBeforeChip + chipWidth;
        visibleCount = nextVisibleCount;
      }

      setSingleRowVisibleCount(visibleCount);
    };

    calculateVisibleChips();

    const resizeObserver = new ResizeObserver(calculateVisibleChips);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [singleRow, chips, maxAllowedChips]);

  const removeChip = (filterKey: string | undefined, optionId: string | undefined, filterKeys?: string[]) => {
    if (isControlled && controlledValues && onChange) {
      const updatedValues: Record<string, string[]> = { ...controlledValues };

      if (filterKeys && filterKeys.length > 0) {
        filterKeys.forEach((key) => {
          updatedValues[key] = [];
        });
        onChange(updatedValues);
        return;
      }

      if (filterKey) {
        const customFilterMatch = customFilters?.find(
          (f) => ('key' in f && f.key === filterKey) || ('keys' in f && f.keys?.includes(filterKey)),
        );

        if (customFilterMatch) {
          updatedValues[filterKey] = [];
          onChange(updatedValues);
          return;
        }
      }

      if (filterKey && optionId) {
        updatedValues[filterKey] = (updatedValues[filterKey] || []).filter((valueItem) => valueItem !== optionId);
        onChange(updatedValues);
      }

      return;
    }

    if (filterKeys && filterKeys.length > 0) {
      pushValuesToRouteQuery(router, {
        ...Object.fromEntries(filterKeys.map((key) => [key, null])),
        page: null,
      });
      return;
    }

    if (filterKey) {
      const customFilterMatch = customFilters?.find(
        (f) => ('key' in f && f.key === filterKey) || ('keys' in f && f.keys?.includes(filterKey)),
      );

      if (customFilterMatch) {
        pushValuesToRouteQuery(router, {
          [filterKey]: null,
          page: null,
        });
        return;
      }
    }

    if (filterKey && optionId) {
      const selectedFilterValues: Record<string, string[]> = {
        [filterKey]: [],
      };

      assignValuesFromRouteQuery(router, selectedFilterValues);

      const updatedValues = (selectedFilterValues[filterKey] || []).filter((value) => value !== optionId);

      pushValuesToRouteQuery(router, {
        [filterKey]: updatedValues,
        page: null,
      });
    }
  };

  if (!chips.length) {
    return null;
  }

  const visibleChipsCount = singleRow ? (singleRowVisibleCount ?? maxAllowedChips) : maxAllowedChips;

  const visibleChips = chips.slice(0, visibleChipsCount);

  const hiddenChipsCount = chips.length - visibleChips.length;

  const renderChip = (chip: FilterChip, measure = false) => (
    <div
      key={chip.filterKeys ? chip.filterKeys.join('-') : `${chip.filterKey}-${chip.optionId}`}
      data-measure-chip={measure || undefined}
      className="inline-flex shrink-0 items-center gap-2 rounded-full border border-black/10 bg-gray-50 pr-2 pl-3 text-sm"
    >
      <span className="mb-0.5 py-1">{chip.label}</span>
      <button
        type="button"
        onClick={measure ? undefined : () => removeChip(chip.filterKey, chip.optionId, chip.filterKeys)}
        className="cursor-pointer rounded-full p-0.5 transition-colors hover:bg-black/5"
        aria-label={`Remove filter ${chip.label}`}
        tabIndex={measure ? -1 : undefined}
      >
        <XLight className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );

  if (singleRow) {
    return (
      <div
        ref={containerRef}
        className={classNames('relative flex min-w-0 items-center gap-2 overflow-hidden', className)}
      >
        <div className="flex min-w-0 flex-nowrap gap-2 overflow-hidden">
          {visibleChips.map((chip) => renderChip(chip))}
        </div>

        {hiddenChipsCount > 0 && (
          <span className="inline-flex shrink-0 items-center py-1 text-sm text-black/60">
            {hiddenChipsCount} more {hiddenChipsCount === 1 ? 'filter' : 'filters'}
          </span>
        )}

        <div
          ref={measureRef}
          aria-hidden
          className="pointer-events-none invisible absolute top-0 left-0 flex flex-nowrap gap-2 whitespace-nowrap"
        >
          {chips.map((chip) => renderChip(chip, true))}
        </div>

        <span
          ref={moreLabelMeasureRef}
          aria-hidden
          className="pointer-events-none invisible absolute top-0 left-0 inline-flex shrink-0 items-center py-1 text-sm whitespace-nowrap text-black/60"
        >
          {chips.length} more filters
        </span>
      </div>
    );
  }

  return (
    <div className={classNames('flex flex-wrap gap-2', className)}>
      {visibleChips.map((chip) => renderChip(chip))}

      {hiddenChipsCount > 0 && (
        <span className="inline-flex items-center py-1 text-sm text-black/60">
          {hiddenChipsCount} more {hiddenChipsCount === 1 ? 'filter' : 'filters'}
        </span>
      )}
    </div>
  );
};

export default SearchFilterChips;
